import { useEffect, useState } from 'react';
import persistedQueries from '@aether/client-graphql/generated/persisted-documents.json';
import { ConsentBanner } from '@aether/tracking/consent-banner';
import { getBffProxyOrigin } from '@aether/utils/services/linking-service';
import { type UserRole } from '@aether/validation';
import { json, type LinksFunction, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/cloudflare';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useMatches,
  useRouteError,
} from '@remix-run/react';
import { captureRemixErrorBoundaryError } from '@sentry/remix';
import { ClientOnly } from 'remix-utils/client-only';

import stylesheet from '~/tailwind.css?url';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { NewFooter } from './components/NewFooter';
import { NewHeader } from './components/NewHeader';

const THEME_COOKIE = 'theme-preference';

export type User = { displayName?: string | null; role: UserRole };
export type Theme = 'light' | 'dark';

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: stylesheet },
  {
    rel: 'preload',
    href: '/fonts/GeistVF.woff2',
    as: 'font',
    type: 'font/woff2',
  },
];

export const meta: MetaFunction = () => {
  return [
    { title: 'AI Dragon' },
    {
      name: 'description',
      content:
        'Create, augment and analyse your portfolio at scale and harness the power of machine learning with our cutting-edge AI.',
    },
    {
      name: 'keywords',
      content: 'Arabesque, Portfolio Dragon, AI, machine learning, portfolio management',
    },
    {
      tagName: 'link',
      rel: 'icon',
      type: 'image/png',
      sizes: '32x32',
      href: '/favicon-32x32.png',
    },
    {
      tagName: 'link',
      rel: 'icon',
      type: 'image/png',
      sizes: '16x16',
      href: '/favicon-16x16.png',
    },
    {
      name: 'og:title',
      content: 'AI Dragon',
    },
    {
      name: 'og:description',
      content:
        'Create, augment and analyse your portfolio at scale and harness the power of machine learning with our cutting-edge AI.',
    },
    {
      name: 'og:image',
      content: 'https://portfoliodragon.com/android-chrome-512x512.png', // TODO: Update with a proper image
    },
    {
      name: 'og:url',
      content: 'https://arabesque.ai/',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'og:type',
      content: 'website',
    },
  ];
};

export const loader = async ({ request, context: { cloudflare } }: LoaderFunctionArgs) => {
  const host = request.headers.get('host');
  const cookieHeader = request.headers.get('Cookie');
  const isLocal = host?.includes('localhost');
  const protocol = request.headers.get('x-forwarded-proto') || 'https';
  const origin = host ? `${protocol}://${host}` : null;

  // let data = null;
  // if (cookieHeader?.includes('session')) {
  //   // workaround for local subdomains see https://github.com/nodejs/node/issues/50871
  //   const url = isLocal ? 'http://127.0.0.1:4000/graphql' : `${getBffProxyOrigin(origin)}/graphql`;
  //   const authQueryHash =
  //     Object.entries(persistedQueries).find(([_, query]) => query.includes('query AuthQuery'))?.[0] || '';

  //   const response = await fetch(url, {
  //     method: 'POST',
  //     headers: {
  //       'Content-Type': 'application/json',
  //       Cookie: cookieHeader,
  //       // workaround for local subdomains see https://github.com/nodejs/node/issues/50871
  //       ...(isLocal ? { host: `bff.arabesque.localhost` } : {}),
  //     },
  //     body: JSON.stringify({
  //       extensions: { persistedQuery: { version: 1, sha256Hash: authQueryHash } },
  //       operationName: 'AuthQuery',
  //     }),
  //   });
  //   data = await response.json<{ data?: { me?: User } }>();
  // }
  const theme: Theme | undefined = cookieHeader
    ?.split(';')
    .map(pair => pair.trim().split('='))
    .find(([key]) => key === THEME_COOKIE)?.[1] as Theme | undefined;

  return json({
    ENV: {
      POSTHOG_KEY: cloudflare.env.POSTHOG_KEY,
      POSTHOG_HOST: cloudflare.env.POSTHOG_HOST,
      BFF_URI: cloudflare.env.BFF_URI,
      SUPPORT_CHAT_ID: cloudflare.env.SUPPORT_CHAT_ID,
    },
    origin,
    // user: data?.data?.me,
    theme,
  });
};

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <div>Something went wrong</div>;
};

export default function Root() {
  const data = useLoaderData<typeof loader>();
  // Set dark mode as default
  const [theme, setTheme] = useState<Theme>(data.theme || 'dark');
  const matches = useMatches();
  const lastMatch = matches[matches.length - 1];
  const { newLayout } = (lastMatch.handle as { newLayout?: boolean }) || {};
  // const isAuthenticated = !!data.user && data?.user?.role !== 'public';
  const isAuthenticated = false;

  const handleThemeChange = (newTheme: Theme) => {
    setTheme(newTheme);
    document.cookie = `${THEME_COOKIE}=${newTheme}; Path=/; Max-Age=${60 * 60 * 24 * 365}`;
    document.documentElement.className = newTheme;
  };

  const htmlClassName = newLayout ? `${theme} scroll-smooth` : undefined;

  return (
    <html lang="en" className={htmlClassName}>
      <head>
        {/* Add script to detect OS theme before React hydration to prevent flash */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
            (function() {
              const cookies = document.cookie.split(';');
              const themeCookie = cookies.find(c => c.trim().startsWith('${THEME_COOKIE}='));
              
              if (!themeCookie) {
                const newLayout = ${JSON.stringify(newLayout)};
                if (newLayout) {
                  document.documentElement.classList.add('dark');
                }
              }
            })();
          `,
          }}
        />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=0.75, maximum-scale=5.0, minimum-scale=0.75, viewport-fit=cover" />
        <Meta />
        <Links />
      </head>
      <body className="min-h-screen dark:bg-gray-950 dark:text-white">
        <div className="m-auto grid h-full min-h-screen w-full grid-rows-[auto_1fr_auto]">
          {newLayout ? (
            <NewHeader
              isAuthenticated={isAuthenticated}
              origin={data.origin}
              currentTheme={theme}
              onThemeChange={handleThemeChange}
            />
          ) : (
            <Header isAuthenticated={isAuthenticated} origin={data.origin} className="px-6 pt-6" />
          )}
          <Outlet />
          {newLayout ? <NewFooter /> : <Footer />}
        </div>
        {/* The SSR consent banner produces hydration errors, most likely because of how we're fetching the cookie */}
        <ClientOnly>{() => <ConsentBanner />}</ClientOnly>
        <ScrollRestoration />
        <script dangerouslySetInnerHTML={{ __html: `window.ENV = ${JSON.stringify(data.ENV)}` }} />
        <Scripts />
      </body>
    </html>
  );
}
